import Panel from '@movable/studio-ar/panel';
import html2canvas from "@movable/html2canvas";
import { cloneToStatic, dataURItoBlob, hideTag, showTag, tagOnClick, sleep } from '@movable/studio-ar/utils';
import FaceTracker from './face-tracker';
import GifWriter from './gif-writer';
import "confetti-js";
//import userMedia from './user-media';

const dpi = window.devicePixelRatio || 1;
let lastStickerIndex = null;

export default class VideoPanel extends Panel {
  render() {

    const canvasContainer = this.getTag('canvas-container').element;
    canvasContainer.style.backgroundColor = '';
    const confettiCanvas = document.createElement('canvas');

    confettiCanvas.setAttribute('width', canvasContainer.style.width);
    confettiCanvas.setAttribute('height', canvasContainer.style.height);
    confettiCanvas.setAttribute('id', 'confetti-holder');
    confettiCanvas.setAttribute('background-color', '');

    canvasContainer.appendChild(confettiCanvas);

    this.confetti = new ConfettiGenerator({
      target: 'confetti-holder',
      max: 140,
      animate: true,
      clock: 25,
      width: parseInt(confettiCanvas.width),
      height: parseInt(confettiCanvas.height),
      colors: [
        [251, 176, 52],
        [80, 184, 72],
        [32, 45, 96],
        [252, 75, 34],
        [17, 169, 219],
        [236, 236, 236]
      ],
      rotate: true,
      size: 1.8,
      props: [
        "circle",
        "square",
        "line"
      ]
    });

    this.parent.replaceTokens(this.tags, this.parent.tokens || {});

    hideTag(this.getTagByTool('countdown'));
    hideTag(this.getTagByTool('frame-indicator'));

    const camera = this.getTagByTool('camera');
    if (!camera) {
      throw new Error('missing camera tag in the video panel');
    }


    return this.setupFaceFilter(camera).then(() => {
      hideTag(this.getTag('canvas-container'));
      return this.setupHandlers();
    });
  }

  // Set up video _without_ face tracking, an alternative to setupFaceFilter.
  setupVideo(camera) {
    return userMedia(camera.element, {
      cameraDirection: this.parent.fields.camera_direction
    }).then(video => {
      this.mirrorVideo = video.srcObject.mirrored;
      return video;
    });
  }

  setupFaceFilter(camera) {
    const videoContainer = camera.element;

    const canvas = document.createElement('canvas');
    canvas.classList.add('face-tracker');
    canvas.classList.add('mirror');
    canvas.width = camera.width * dpi;
    canvas.height = camera.height * dpi;

    videoContainer.innerHTML = '';
    videoContainer.appendChild(canvas);

    const faceTrackImages = this.tags.filter(t => t.classNames.match("face-track-image"));
    faceTrackImages.forEach(hideTag);

    this.tracker = new FaceTracker(canvas, {
      trackedImages: faceTrackImages,
      onFaceDetectionChange: this.onFaceDetectionChange.bind(this),
      onMouthDetectionChange: this.onMouthDetectionChange.bind(this)
    });

    return this.tracker.init();
  }

  onMouthDetectionChange(isDetected, threePlane) {
    
    const stickers = [];

    threePlane.children.forEach(child => {
      if (child.mouthOpenOnly && !child.userData.imageClassNames.match(/sticker/)) {
        child.visible = isDetected;
        const confettiCanvas = document.querySelector('.canvas-container');
        if(confettiCanvas.style.display !== 'block'){
          showTag(this.getTag('canvas-container'));
          // hideTag(this.getTag('prompt'));
          this.confetti.render();
        }
      }else if(child.mouthOpenOnly && child.userData.imageClassNames.match(/sticker/)){
        stickers.push(child);
        child.visible = false; // display none so we can randomize on every mouth open
      }
    });

    if(stickers.length && isDetected){
      
      let stickerIndex = (!lastStickerIndex) ? 3 : Math.floor(Math.random()*stickers.length);
      stickerIndex = (stickerIndex === lastStickerIndex) ? stickerIndex + 1 : stickerIndex;
      stickerIndex = (stickers[stickerIndex]) ? stickerIndex : 0;
      lastStickerIndex = stickerIndex;

      stickers[stickerIndex].visible = true;

    }

    if(isDetected){
      hideTag(this.getTag('prompt'));
    }else{
      showTag(this.getTag('prompt'));
    }

  }

  onFaceDetectionChange(isDetected, face) {
    face.visible = isDetected;

    const silhouette = this.getTag('silhouette');
    if (silhouette) {
      silhouette.element.classList[isDetected ? 'add' : 'remove']('hidden');
    }
  };

  setupHandlers() {
    tagOnClick(this.getTagByTool('record'), () => {
      const photoType = this.parent.fields.photo_type;
      const takeMethod = (photoType === 'gif4') ? 'takeMultiple' : 'takeSingle';

      hideTag(this.getTagByTool('record'));
      hideTag(this.getTag('tap-for-photo'));
      hideTag(this.getTag('hide-for-photo'));
      hideTag(this.getTag('silhouette'));
      hideTag(this.getTag('outline'));
      hideTag(this.getTag('prompt'));

      this[takeMethod]().then(blob => {
        this.parent.sendEvent('take_picture');
        this.parent.imageDataURL = window.URL.createObjectURL(blob);
        this.parent.blobby = blob;

        this.parent.transitionToPanel('review');
      });
    });
  }

  takeSingle() {
    return this.takePicture().then(canvas => {
      return new Promise(resolve => {
        return canvas.toBlob(resolve, 'image/jpeg', 0.85);
      });
    });
  }

  async takeMultiple(frameCount=4) {
    const { width, height } = this.tag;

    const delay = Math.round((parseInt(this.parent.fields.gif_interval, 10) || 500) / 10);

    const gifLoop = true;
    const writer = new GifWriter(width, height, frameCount, {
      loop: gifLoop ? 0 : 1,
      delay
    });

    for(let i = 0; i < frameCount; i++) {
      this.displayFrameIndicator(i);
      await this.countdown(3, 1000);
      writer.addFrame(await this.takePicture());
    }

    return writer.toBlob();
  }

  displayFrameIndicator(frame) {
    const index = frame + 1; // starts from 1
    const frameIndicator = this.getTagByTool('frame-indicator');
    if (!frameIndicator) { return; }

    showTag(frameIndicator);

    const classList = frameIndicator.element.classList;
    classList.forEach(className => {
      if (className.indexOf('frame-') === 0) {
        classList.remove(className);
      }
    });

    frameIndicator.element.classList.add(`frame-${index}`);
  }

  takePicture() {

    const otherPanels = this.parent.panels.filter(p => p.panelName !== 'video');
    otherPanels.forEach(p => p.hide());
    hideTag(this.getTagByTool('frame-indicator'));

    return html2canvas(this.tag.element, {useCORS: true}).then(canvas => {
      otherPanels.forEach(p => p.show());
      return canvas;
    }).catch(e => {
      otherPanels.forEach(p => p.show());
      throw(e);
    });
  }

  async countdown(fromCount, interval) {
    const countdownTag = this.getTagByTool('countdown');
    if (!countdownTag) {
      throw("Cannot count down without Countdown tag present");
    }
    const { element } = countdownTag;
    showTag(countdownTag);

    while (fromCount > 0) {
      element.innerText = '' + fromCount;
      await sleep(interval);

      fromCount--;
    }
    hideTag(countdownTag);
  }

  reset() {
    const containerElement = this.getTagByTool('camera').element;

    const clone = containerElement.querySelector('div');
    if (clone) clone.remove();

    const videoElement = containerElement.querySelector('canvas.face-tracker');
    if (videoElement) videoElement.style.display = 'block';

    showTag(this.getTagByTool('record'));
    showTag(this.getTag('tap-for-photo'));
    showTag(this.getTag('silhouette'));
    showTag(this.getTag('outline'));
    showTag(this.getTag('prompt'));
  }

  activate() {
    this.tracker && this.tracker.toggleSlow(false);
  }

  deactivate() {
    this.tracker && this.tracker.toggleSlow(true);
    this.reset();
  }
}
